import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ListChecks, ArrowClockwise, XCircle } from "@phosphor-icons/react";
import { PromoPageHeader } from "@/promotions/components/PromoPageHeader";
import { PromoSelect } from "@/promotions/components/PromoSelect";
import { EmptyState } from "@/promotions/components/EmptyState";
import { StatusBadge } from "@/promotions/components/StatusBadge";
import { TableSkeleton } from "@/promotions/components/Skeleton";
import { ConfirmDialog } from "@/promotions/components/campaign/ConfirmDialog";
import { apiError } from "@/lib/api";
import { useCreativeData, creativeApi } from "../useCreative";
import { fmtDateTime, fmtCost } from "../creativeUtils";

const CANCELLABLE = ["queued", "running"];

export default function Jobs() {
  const { jobs, loading, reload } = useCreativeData();
  const [status, setStatus] = useState("");
  const [toCancel, setToCancel] = useState(null);

  const filtered = useMemo(() => jobs.filter((j) => !status || j.status === status), [jobs, status]);

  const confirmCancel = async () => {
    try {
      await creativeApi.cancelJob(toCancel.id);
      toast.success("Job cancelled");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setToCancel(null);
    }
  };

  return (
    <div className="space-y-8" data-testid="creative-jobs">
      <PromoPageHeader
        overline="Creative Studio"
        title="Jobs"
        description="Every generation run — provider, cost, status and lifecycle."
        actions={
          <button onClick={reload} className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground" data-testid="jobs-refresh">
            <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <PromoSelect
          value={status}
          onChange={setStatus}
          placeholder="All statuses"
          className="w-48"
          options={[
            { value: "", label: "All statuses" },
            { value: "queued", label: "Queued" },
            { value: "running", label: "Running" },
            { value: "completed", label: "Completed" },
            { value: "failed", label: "Failed" },
            { value: "cancelled", label: "Cancelled" },
            { value: "dead_letter", label: "Dead letter" },
          ]}
          testId="jobs-filter-status"
        />
        <span className="text-sm text-muted-foreground" data-testid="jobs-count">{filtered.length} job{filtered.length === 1 ? "" : "s"}</span>
      </div>

      {loading ? (
        <TableSkeleton rows={6} cols={6} />
      ) : filtered.length === 0 ? (
        <EmptyState Icon={ListChecks} title="No jobs found" description="Generation jobs will appear here." />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="jobs-table">
              <thead className="promo-glass border-b border-border">
                <tr>
                  {["Job", "Kind", "Provider", "Status", "Attempts", "Cost", "Created", ""].map((h) => (
                    <th key={h} className="px-4 py-3 text-left promo-overline whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((j) => (
                  <tr key={j.id} className="border-b border-border/60 last:border-0 hover:bg-accent/30" data-testid={`job-row-${j.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{j.id.slice(0, 8)}</td>
                    <td className="px-4 py-3 capitalize">{j.brief?.kind || "—"} · {j.brief?.operation || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{j.provider || "—"}</td>
                    <td className="px-4 py-3"><StatusBadge status={j.status} /></td>
                    <td className="px-4 py-3 tabular-nums">{j.attempts}/{j.max_attempts}</td>
                    <td className="px-4 py-3 tabular-nums">{fmtCost(j.resolved_cost)}</td>
                    <td className="px-4 py-3 tabular-nums text-muted-foreground">{fmtDateTime(j.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      {CANCELLABLE.includes(j.status) && (
                        <button
                          onClick={() => setToCancel(j)}
                          className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:border-destructive/40 hover:text-destructive"
                          data-testid={`job-cancel-${j.id}`}
                        >
                          <XCircle size={13} /> Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!toCancel}
        danger
        title="Cancel job?"
        description={toCancel ? `Job ${toCancel.id.slice(0, 8)} will be cancelled and its reservation released.` : ""}
        confirmLabel="Cancel job"
        cancelLabel="Keep running"
        onConfirm={confirmCancel}
        onCancel={() => setToCancel(null)}
      />
    </div>
  );
}
